<script setup lang="ts">
import { SellingMethod } from '~/apiClient';

// COMPOSABLES
const { tt } = useTypedI18n();
const sellingMethod = useSlpSellingMethod();
const {
    // Filters
    alloyWheels,
    asr,
    axles,
    busType,
    chargingType,
    companionSeatsFrom,
    companionSeatsTo,
    countryCodes,
    cruiseControl,
    doubleDecker,
    deckType,
    displacementFrom,
    displacementTo,
    doors,
    driverBerth,
    engineBrand,
    enginePosition,
    euroNorm,
    firstRegistrationYearFrom,
    firstRegistrationYearTo,
    foldingSeatsFrom,
    foldingSeatsTo,
    fuel,
    fuelTankCapacityFrom,
    fuelTankCapacityTo,
    gearboxBrand,
    gearboxType,
    glasses,
    heightFrom,
    heightTo,
    kmFrom,
    kmTo,
    kwFrom,
    kwTo,
    lengthFrom,
    lengthTo,
    microphone,
    monitors,
    openableWindowsFrom,
    openableWindowsTo,
    passThroughLuggageCompartment,
    platformBusClass,
    preHeater,
    priceFrom,
    priceTo,
    registrationDocumentCountry,
    spareWheel,
    retarder,
    saleType,
    seatsFrom,
    seatsTo,
    seatUpholstery,
    socket220,
    standingPlacesFrom,
    standingPlacesTo,
    aState,
    dsState,
    useType,
    tachograph,
    toilet,
    totalCapacityFrom,
    totalCapacityTo,
    vehicleCondition,
    wheelchairPlacesFrom,
    wheelchairPlacesTo,
    wheelchairRamp,
    wheels,
    wheelsSizeAxle1,
    wheelsSizeAxle2,
    wheelsSizeAxle3,
    widthFrom,
    widthTo,

    // OPTIONS
    axlesOptions,
    busTypeOptions,
    chargingTypeOptions,
    companionSeatsFromOptions,
    companionSeatsToOptions,
    countryCodesOptions,
    cruiseControlOptions,
    deckTypeOptions,
    displacementFromOptions,
    displacementToOptions,
    doorsOptions,
    engineBrandOptions,
    enginePositionOptions,
    euroNormOptions,
    firstRegistrationYearFromOptions,
    firstRegistrationYearToOptions,
    foldingSeatsFromOptions,
    foldingSeatsToOptions,
    fuelOptions,
    fuelTankCapacityFromOptions,
    fuelTankCapacityToOptions,
    gearboxBrandOptions,
    gearboxTypeOptions,
    glassesOptions,
    heightFromOptions,
    heightToOptions,
    kmFromOptions,
    kmToOptions,
    kwFromOptions,
    kwToOptions,
    lengthFromOptions,
    lengthToOptions,
    monitorsOptions,
    openableWindowsFromOptions,
    openableWindowsToOptions,
    platformBusClassOptions,
    priceFromOptions,
    priceToOptions,
    registrationDocumentCountryOptions,
    saleTypeOptions,
    seatsFromOptions,
    seatsToOptions,
    seatUpholsteryOptions,
    standingPlacesFromOptions,
    standingPlacesToOptions,
    aStateOptions,
    dsStateOptions,
    tachographOptions,
    totalCapacityFromOptions,
    totalCapacityToOptions,
    useTypeOptions,
    vehicleConditionOptions,
    wheelchairPlacesFromOptions,
    wheelchairPlacesToOptions,
    wheelchairRampOptions,
    wheelsOptions,
    wheelsSizeAxle1Options,
    wheelsSizeAxle2Options,
    wheelsSizeAxle3Options,
    widthFromOptions,
    widthToOptions,
} = useSlpParams(sellingMethod);
const { onSlpFilterChangeByValue } = useTracking();

// DATA
const cardUi = { header: '[&>h3]:text-xl [&>h3]:font-bold', root: 'divide-y-0', body: 'gap-6 divide-y *:not-[last-child]:pb-6' };
const checkboxGroupUi = { fieldset: 'md:grid md:grid-cols-4 md:gap-x-3 md:gap-y-6' };
</script>

<template>
    <UCard :ui="cardUi" variant="outline">
        <template #header>
            <h3>{{ tt('slp.filters.advanced.sectionType.title') }}</h3>
        </template>

        <SlpFiltersAdvancedSection :title="tt(`slp.${sellingMethod}.filters.state.title`)">
            <template v-if="sellingMethod === SellingMethod.Auction">
                <UCheckboxGroup v-model="aState" class="md:col-span-4" :ui="checkboxGroupUi" :items="aStateOptions" size="xl" />
            </template>
            <template v-if="sellingMethod === SellingMethod.DirectSale">
                <UCheckboxGroup v-model="dsState" class="md:col-span-4" :ui="checkboxGroupUi" :items="dsStateOptions" size="xl" />
            </template>
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.saleType.base')">
            <UInputMenu v-model="saleType" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="saleTypeOptions" value-key="value" />
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.scrapping.title')">
            <SlpFiltersFieldScrapping />
        </SlpFiltersAdvancedSection>
    </UCard>
    <UCard :ui="cardUi" variant="outline">
        <template #header>
            <h3>{{ tt('slp.filters.advanced.sectionVehicleData.title') }}</h3>
        </template>
        <div class="space-y-6">
            <SlpFiltersAdvancedSection :title="tt('slp.filters.busType')">
                <UCheckboxGroup
                    v-model="busType"
                    :items="busTypeOptions"
                    size="xl"
                    class="md:col-span-4"
                    :ui="checkboxGroupUi"
                    @update:model-value="onSlpFilterChangeByValue('type', $event)"
                />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.class')">
                <UCheckboxGroup v-model="platformBusClass" :items="platformBusClassOptions" size="xl" class="md:col-span-4" :ui="checkboxGroupUi" />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.brand')">
                <SlpFiltersFieldBrandModelVersion />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.countryCodes')">
                <UInputMenu
                    v-model="countryCodes"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="countryCodesOptions"
                    value-key="value"
                />
            </SlpFiltersAdvancedSection>
        </div>
        <div class="space-y-6">
            <SlpFiltersAdvancedSection :title="tt('slp.filters.height')">
                <SlpFiltersFieldFromTo
                    v-model:from="heightFrom"
                    v-model:to="heightTo"
                    :items-from="heightFromOptions"
                    :items-to="heightToOptions"
                    :any-label="tt('slp.filters.heightAnyOption')"
                />
                <UCheckbox v-model="doubleDecker" class="mt-8" :label="tt('slp.filters.doubleDecker')" size="xl" />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.length')">
                <SlpFiltersFieldFromTo
                    v-model:from="lengthFrom"
                    v-model:to="lengthTo"
                    :items-from="lengthFromOptions"
                    :items-to="lengthToOptions"
                    :any-label="tt('slp.filters.lengthAnyOption')"
                />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.width')">
                <SlpFiltersFieldFromTo
                    v-model:from="widthFrom"
                    v-model:to="widthTo"
                    :items-from="widthFromOptions"
                    :items-to="widthToOptions"
                    :any-label="tt('slp.filters.widthAnyOption')"
                />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.deckType')">
                <UFormField>
                    <UInputMenu
                        v-model="deckType"
                        :placeholder="tt('slp.filters.inputPlaceholder')"
                        multiple
                        :items="deckTypeOptions"
                        value-key="value"
                    />
                </UFormField>
            </SlpFiltersAdvancedSection>
        </div>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.firstRegistrationYear')">
            <SlpFiltersFieldFromTo
                v-model:from="firstRegistrationYearFrom"
                v-model:to="firstRegistrationYearTo"
                :items-from="firstRegistrationYearFromOptions"
                :items-to="firstRegistrationYearToOptions"
                :from-label="tt('slp.filters.firstRegistrationYearFrom')"
                :to-label="tt('slp.filters.firstRegistrationYearTo')"
                :any-label="tt('slp.filters.firstRegistrationYearAnyOption')"
            />
            <UFormField :label="tt('slp.filters.registrationDocumentCountry')">
                <UInputMenu
                    v-model="registrationDocumentCountry"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="registrationDocumentCountryOptions"
                    value-key="value"
                />
            </UFormField>
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.km')">
            <SlpFiltersFieldFromTo
                v-model:from="kmFrom"
                v-model:to="kmTo"
                :items-from="kmFromOptions"
                :items-to="kmToOptions"
                :any-label="tt('slp.filters.kmAnyOption')"
            />
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.price')">
            <SlpFiltersFieldFromTo
                v-model:from="priceFrom"
                v-model:to="priceTo"
                :items-from="priceFromOptions"
                :items-to="priceToOptions"
                :any-label="tt('slp.filters.priceAnyOption')"
            />
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.advanced.sectionVehicleData.vehicleCondition')">
            <UFormField :label="tt('slp.filters.vehicleCondition')">
                <UInputMenu
                    v-model="vehicleCondition"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="vehicleConditionOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.useType')">
                <UInputMenu v-model="useType" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="useTypeOptions" value-key="value" />
            </UFormField>
        </SlpFiltersAdvancedSection>
    </UCard>
    <UCard :ui="cardUi" variant="outline">
        <template #header>
            <h3>{{ tt('slp.filters.advanced.sectionSpecificationsAndEquipment.title') }}</h3>
        </template>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.mechanicsAndTransmission')">
            <UFormField :label="tt('slp.filters.euroNorm')">
                <UInputMenu
                    v-model="euroNorm"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="euroNormOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.fuel')">
                <UInputMenu v-model="fuel" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="fuelOptions" value-key="value" />
            </UFormField>
            <UFormField :label="tt('slp.filters.engineBrand')">
                <UInputMenu
                    v-model="engineBrand"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="engineBrandOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.enginePosition')">
                <UInputMenu
                    v-model="enginePosition"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="enginePositionOptions"
                    value-key="value"
                />
            </UFormField>
            <SlpFiltersFieldFromTo
                v-model:from="fuelTankCapacityFrom"
                v-model:to="fuelTankCapacityTo"
                :items-from="fuelTankCapacityFromOptions"
                :items-to="fuelTankCapacityToOptions"
                :from-label="tt('slp.filters.fuelTankCapacityFrom')"
                :to-label="tt('slp.filters.fuelTankCapacityTo')"
            />
            <UFormField :label="tt('slp.filters.gearboxType')">
                <UInputMenu
                    v-model="gearboxType"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="gearboxTypeOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.gearboxBrand')">
                <UInputMenu
                    v-model="gearboxBrand"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="gearboxBrandOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.chargingType')">
                <UInputMenu
                    v-model="chargingType"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="chargingTypeOptions"
                    value-key="value"
                />
            </UFormField>
            <UCheckbox v-model="retarder" class="mt-8" :label="tt('slp.filters.retarder')" size="xl" />
            <UCheckbox v-model="asr" class="mt-8" :label="tt('slp.filters.asr')" size="xl" />
        </SlpFiltersAdvancedSection>
        <div class="md:grid md:grid-cols-2 md:gap-x-3">
            <SlpFiltersAdvancedSection columns="2" :title="tt('slp.filters.displacement')">
                <SlpFiltersFieldFromTo
                    v-model:from="displacementFrom"
                    v-model:to="displacementTo"
                    :items-from="displacementFromOptions"
                    :items-to="displacementToOptions"
                />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection columns="2" :title="tt('slp.filters.kilowatts')">
                <SlpFiltersFieldFromTo
                    v-model:from="kwFrom"
                    v-model:to="kwTo"
                    :items-from="kwFromOptions"
                    :items-to="kwToOptions"
                    :any-label="tt('slp.filters.kwAnyOption')"
                />
            </SlpFiltersAdvancedSection>
        </div>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.axlesAndTyres')">
            <UFormField :label="tt('slp.filters.axles')">
                <UInputMenu v-model="axles" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="axlesOptions" value-key="value" />
            </UFormField>
            <UFormField :label="tt('slp.filters.wheels')">
                <UInputMenu v-model="wheels" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="wheelsOptions" value-key="value" />
            </UFormField>
            <UCheckbox v-model="spareWheel" class="mt-8" :label="tt('slp.filters.spareWheel')" size="xl" />
            <UCheckbox v-model="alloyWheels" class="mt-8" :label="tt('slp.filters.alloyWheels')" size="xl" />
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.tyreSizePerAxle')">
            <UFormField :label="tt('slp.filters.wheelsSizeAxle1')">
                <UInputMenu
                    v-model="wheelsSizeAxle1"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="wheelsSizeAxle1Options"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.wheelsSizeAxle2')">
                <UInputMenu
                    v-model="wheelsSizeAxle2"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="wheelsSizeAxle2Options"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.wheelsSizeAxle3')">
                <UInputMenu
                    v-model="wheelsSizeAxle3"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="wheelsSizeAxle3Options"
                    value-key="value"
                />
            </UFormField>
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.doors')">
            <UFormField>
                <UInputMenu v-model="doors" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="doorsOptions" value-key="value" />
            </UFormField>
        </SlpFiltersAdvancedSection>
        <div class="space-y-6 md:grid md:grid-cols-2 md:gap-x-3 md:gap-y-6">
            <SlpFiltersAdvancedSection :title="tt('slp.filters.totalCapacity')" columns="2">
                <SlpFiltersFieldFromTo
                    v-model:from="totalCapacityFrom"
                    v-model:to="totalCapacityTo"
                    :items-from="totalCapacityFromOptions"
                    :items-to="totalCapacityToOptions"
                />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.seats')" columns="2">
                <SlpFiltersFieldFromTo v-model:from="seatsFrom" v-model:to="seatsTo" :items-from="seatsFromOptions" :items-to="seatsToOptions" />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection columns="2" :title="tt('slp.filters.standingPlaces')">
                <SlpFiltersFieldFromTo
                    v-model:from="standingPlacesFrom"
                    v-model:to="standingPlacesTo"
                    :items-from="standingPlacesFromOptions"
                    :items-to="standingPlacesToOptions"
                />
            </SlpFiltersAdvancedSection>
        </div>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.openableWindows')">
            <SlpFiltersFieldFromTo
                v-model:from="openableWindowsFrom"
                v-model:to="openableWindowsTo"
                :items-from="openableWindowsFromOptions"
                :items-to="openableWindowsToOptions"
            />
        </SlpFiltersAdvancedSection>
        <div class="md:grid md:grid-cols-2 md:gap-x-3">
            <SlpFiltersAdvancedSection :title="tt('slp.filters.foldingSeats')" columns="2">
                <SlpFiltersFieldFromTo
                    v-model:from="foldingSeatsFrom"
                    v-model:to="foldingSeatsTo"
                    :items-from="foldingSeatsFromOptions"
                    :items-to="foldingSeatsToOptions"
                />
            </SlpFiltersAdvancedSection>
            <SlpFiltersAdvancedSection :title="tt('slp.filters.companionSeats')" columns="2">
                <SlpFiltersFieldFromTo
                    v-model:from="companionSeatsFrom"
                    v-model:to="companionSeatsTo"
                    :items-from="companionSeatsFromOptions"
                    :items-to="companionSeatsToOptions"
                />
            </SlpFiltersAdvancedSection>
        </div>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.wheelchairPlaces')">
            <SlpFiltersFieldFromTo
                v-model:from="wheelchairPlacesFrom"
                v-model:to="wheelchairPlacesTo"
                :items-from="wheelchairPlacesFromOptions"
                :items-to="wheelchairPlacesToOptions"
            />
            <UFormField :label="tt('slp.filters.wheelchairRamp')">
                <UInputMenu
                    v-model="wheelchairRamp"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="wheelchairRampOptions"
                    value-key="value"
                />
            </UFormField>
        </SlpFiltersAdvancedSection>
        <SlpFiltersAdvancedSection :title="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.accessoriesAndComfort')">
            <UFormField :label="tt('slp.filters.seatUpholstery')">
                <UInputMenu
                    v-model="seatUpholstery"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="seatUpholsteryOptions"
                    value-key="value"
                />
            </UFormField>
            <SlpFiltersFieldBooleanMulti
                :label="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.onBoardServices')"
                :fields="['kitchen', 'coffeeMachine', 'refrigerator']"
            />
            <SlpFiltersFieldBooleanMulti
                :label="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.connectivityAndMedia')"
                :fields="['mp3', 'cdPlayer', 'dvdPlayer', 'usbPlayer', 'seatsMonitor', 'seatsRadio', 'seatsUsbPorts', 'wifi']"
            />
            <SlpFiltersFieldBooleanMulti
                :label="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.passengersComfort')"
                :fields="[
                    'handRest',
                    'footRest',
                    'hatBox',
                    'recliningSeats',
                    'extendableSeats',
                    'curtains',
                    'coffeeTables',
                    'seatPocket',
                    'seatsSocket220',
                ]"
            />
            <UFormField :label="tt('slp.filters.tachograph')">
                <UInputMenu
                    v-model="tachograph"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="tachographOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.cruiseControl')">
                <UInputMenu
                    v-model="cruiseControl"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="cruiseControlOptions"
                    value-key="value"
                />
            </UFormField>
            <UFormField :label="tt('slp.filters.glasses')">
                <UInputMenu v-model="glasses" :placeholder="tt('slp.filters.inputPlaceholder')" multiple :items="glassesOptions" value-key="value" />
            </UFormField>
            <SlpFiltersFieldBooleanMulti
                :label="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.safetyDevices')"
                :fields="['firePreventionSystem', 'indicatorSigns', 'safetyBelts', 'breathalyzer']"
            />
            <SlpFiltersFieldBooleanMulti
                :label="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.ac')"
                :fields="['acDriver', 'acPassengers', 'heater']"
            />
            <SlpFiltersFieldBooleanMulti
                :label="tt('slp.filters.advanced.sectionSpecificationsAndEquipment.videocameras')"
                :fields="['cctv', 'internalCameras', 'externalCameras', 'retrocamera']"
            />
            <UFormField :label="tt('slp.filters.monitors')">
                <UInputMenu
                    v-model="monitors"
                    :placeholder="tt('slp.filters.inputPlaceholder')"
                    multiple
                    :items="monitorsOptions"
                    value-key="value"
                />
            </UFormField>
            <UCheckbox v-model="driverBerth" class="mt-8" :label="tt('slp.filters.driverBerth')" size="xl" />
            <UCheckbox v-model="socket220" class="mt-8" :label="tt('slp.filters.socket220')" size="xl" />
            <UCheckbox v-model="microphone" class="mt-8" :label="tt('slp.filters.microphone')" size="xl" />
            <UCheckbox v-model="toilet" class="mt-8" :label="tt('slp.filters.toilet')" size="xl" />
            <UCheckbox v-model="preHeater" class="mt-8" :label="tt('slp.filters.preHeater')" size="xl" />
            <UCheckbox v-model="passThroughLuggageCompartment" class="mt-8" :label="tt('slp.filters.passThroughLuggageCompartment')" size="xl" />
        </SlpFiltersAdvancedSection>
    </UCard>
</template>
